import React, { useState } from 'react';
import { Save, CheckCircle, SkipForward, Loader2 } from 'lucide-react';
import type { StepStatus } from '../../types/verification';
import './VerificationStepActionBar.css';

interface VerificationStepActionBarProps {
  stepStatus: StepStatus;
  isSaving: boolean;
  // Save handlers resolve a success flag (SLV2-3155) or nothing — the bar
  // ignores the value; failures are surfaced through the wizard's saveError.
  onSaveChanges: () => Promise<boolean | void>;
  onNoChanges: () => void;
  onSkip: (notes?: string) => void;
}

const VerificationStepActionBar: React.FC<Readonly<VerificationStepActionBarProps>> = ({
  stepStatus,
  isSaving,
  onSaveChanges,
  onNoChanges,
  onSkip,
}) => {
  const [showSkipNotes, setShowSkipNotes] = useState(false);
  const [skipNotes, setSkipNotes] = useState('');

  const handleSkip = () => {
    if (showSkipNotes) {
      onSkip(skipNotes || undefined);
      setShowSkipNotes(false);
      setSkipNotes('');
    } else {
      setShowSkipNotes(true);
    }
  };

  const statusLabel: Record<StepStatus, { text: string; className: string }> = {
    pending: { text: 'Not yet reviewed', className: 'status-pending' },
    confirmed: { text: 'Confirmed - No changes', className: 'status-confirmed' },
    updated: { text: 'Updated & saved', className: 'status-updated' },
    skipped: { text: 'Skipped', className: 'status-skipped' },
  };

  const current = statusLabel[stepStatus];

  return (
    <div className="verification-action-bar">
      <div className="action-bar-status">
        <span className={`status-indicator ${current.className}`} />
        <span className="status-text">{current.text}</span>
      </div>

      <div className="action-bar-buttons">
        <button
          type="button"
          className="action-btn btn-save"
          onClick={onSaveChanges}
          disabled={isSaving}
        >
          {isSaving ? (
            <><Loader2 size={16} className="spinner" /> Saving...</>
          ) : (
            <><Save size={16} /> Save Changes</>
          )}
        </button>

        <button
          type="button"
          className="action-btn btn-confirm"
          onClick={onNoChanges}
          disabled={isSaving}
        >
          <CheckCircle size={16} />
          No Changes Needed
        </button>

        <button
          type="button"
          className="action-btn btn-skip"
          onClick={handleSkip}
          disabled={isSaving}
        >
          <SkipForward size={16} />
          {showSkipNotes ? 'Confirm Skip' : 'Unable to Verify'}
        </button>
      </div>

      {showSkipNotes && (
        <div className="skip-notes-row">
          <input
            type="text"
            placeholder="Optional: reason for skipping..."
            value={skipNotes}
            onChange={(e) => setSkipNotes(e.target.value)}
            className="skip-notes-input"
            autoFocus
            aria-label="Reason for skipping"
          />
          <button
            type="button"
            className="btn-cancel-skip"
            onClick={() => { setShowSkipNotes(false); setSkipNotes(''); }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default VerificationStepActionBar;
